import React, { useEffect, useState } from "react";
import { LOGO_URL as logo } from "../../../services";
import CustomInput from "../../../components/CustomInput/CustomInput";
import { useNavigate } from "react-router-dom";
import Services from "../../../services/Services";
import Spinner from "../../../components/Spinner/Spinner";
import SuccessModal from "../../../components/SuccessModal/SuccessModal";
import PhoneInput from "../../../components/PhoneInput/PhoneInput";
import OTPInput from "../../../components/OTPInput/OTPInput";
import ContractAgreementCheckbox from "../../../components/ContractAgreementCheckbox/ContractAgreementCheckbox";
import {
    getNationalNumberValidationMessage,
    isValidNationalNumber,
    normalizeCountryCode,
    normalizeNationalNumber
} from "../../../utils/phone";
import { getPortal } from "../../../utils/portal";
import { klavyeIleTikla } from "../../../utils/a11y"; // FE-78: tıklanabilir öğelerde klavye desteği
import { COK_DENEME_MESAJI, cokDenemeMi } from "../../../utils/apiOturum";

// 26.10.2026 (güvenlik manifesti A1): BE aynı numaraya 10 dakikada 3 SMS kabul ediyor → "Kodu Tekrar Gönder"
// her gönderimden sonra en az bu kadar saniye kapalı. SMS metni (buildOtpMessage) BE'nin tek izinli şablonudur, değiştirilmemeli.
const TEKRAR_GONDER_SN = 60;

// Müşteri domaininde (my.*) kayıt = doğrudan MÜŞTERİ kaydı ("Kayıt Formu"); diğer
// domainlerde mevcut merchant başvurusu ("Başvuru Formu"). Metin ve gönderilen tip buna göre.
const SIGNUP_COPY = {
    customer: {
        title: "Üye Ol",
        subtitle: "Hemen üye olun, size en uygun depolama çözümünü birlikte bulalım.",
        button: "Üye Ol",
        successTitle: "Kaydınız Oluşturuldu",
        successMessage: "Hesabınız oluşturuldu. Giriş bilgileriniz e-posta adresinize gönderildi.",
    },
    default: {
        title: "Başvuru Formu",
        subtitle: "Türkiye'nin depolama ağına katılmak için ilk adımı atın.",
        button: "Başvur",
        successTitle: "Başvurunuz Başarıyla Alındı",
        successMessage: "Kayıt başvurunuz bize ulaştı. Teşekkür ederiz.",
    },
};

const SignupFormV2 = () => {
    const navigate = useNavigate();
    // Portal her render'da okunur (gerçek domainde sabit; localde ?portal ile SPA gezinmede
    // de doğru çalışsın diye modül seviyesinde DEĞİL burada hesaplanır).
    const isCustomerPortal = getPortal() === "customer";
    const registerType = isCustomerPortal ? "customer" : "merchant";
    const copy = isCustomerPortal ? SIGNUP_COPY.customer : SIGNUP_COPY.default;
    const [kvkkAgreement, setKvkkAgreement] = useState(false);
    const [termsAgreement, setTermsAgreement] = useState(false);
    const [token, setToken] = useState("");
    const [name, setName] = useState("");
    const [lastname, setLastname] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    // Merchant başvurusunda alınan serbest metin ("başvuru yeri, konu vb."). Müşteri kaydında kullanılmaz.
    const [applicationNote, setApplicationNote] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [countryCode, setCountryCode] = useState("90");
    const [errors, setErrors] = useState({ email: "", phone: "" });
    const [showError, setShowError] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState(
        "Bir hata meydana geldi! Lütfen daha sonra tekrar deneyin."
    );
    // OTP adımı: 0=form, 1=doğrulama kodu. Kod FE'de üretilir, send-sms ile gönderilir,
    // girilen kod local karşılaştırılır (kod tabanının SignupForm.js'deki mevcut konvansiyonu).
    const [currentStep, setCurrentStep] = useState(0);
    const [sentOtp, setSentOtp] = useState("");
    const [otpResetKey, setOtpResetKey] = useState(0);
    const [otp, setOtp] = useState("");
    const [generatedPassword, setGeneratedPassword] = useState("");
    const [tekrarKalan, setTekrarKalan] = useState(0); // "Kodu Tekrar Gönder" bekleme süresi (sn)

    useEffect(() => {
        if (tekrarKalan <= 0) return undefined;
        const t = setTimeout(() => setTekrarKalan((k) => k - 1), 1000);
        return () => clearTimeout(t);
    }, [tekrarKalan]);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const validateEmail = () => {
        if (!email) return setErrors((e) => ({ ...e, email: "" }));
        if (!emailRegex.test(email))
            return setErrors((e) => ({
                ...e,
                email: "Geçerli bir e-posta giriniz."
            }));
        setErrors((e) => ({ ...e, email: "" }));
    };

    // TR duyarlı: JS'in varsayılan toUpperCase'i "i" harfini "I" yapıyor ("ibrahim" → "Ibrahim").
    const capitalizeWords = (str) => {
        return String(str ?? "")
            .split(" ")
            .map((word) => (word ? word.charAt(0).toLocaleUpperCase("tr-TR") + word.slice(1) : ""))
            .join(" ");
    };

    const generateRandomSixDigitNumber = () => {
        return Math.floor(100000 + Math.random() * 900000);
    };

    const allFieldsAreValid = () => {
        return (
            kvkkAgreement &&
            termsAgreement &&
            name.trim() &&
            lastname.trim() &&
            email.trim() &&
            phone.trim() &&
            isValidNationalNumber(countryCode, phone) &&
            countryCode.length > 0 &&
            // Merchant başvurusunda not zorunlu; müşteri kaydında bu alan yok.
            (isCustomerPortal || applicationNote.trim().length > 0) &&
            !errors.email &&
            !errors.phone
        );
    };

    const buildOtpMessage = (code) =>
        `Troy Depo dogrulama kodunuz: ${code}. Kodu kimseyle paylasmayin.`;

    // 1. ADIM: form geçerliyse OTP üret + send-sms ile gönder, doğrulama adımına geç.
    const handleSendOtp = () => {
        const emailError =
            email && !emailRegex.test(email) ? "Geçerli bir e-posta giriniz." : "";
        const phoneError =
            phone && !isValidNationalNumber(countryCode, phone)
                ? getNationalNumberValidationMessage(countryCode)
                : "";
        setErrors((prev) => ({ ...prev, email: emailError, phone: phoneError }));
        if (emailError || phoneError) return;
        if (!allFieldsAreValid()) return;

        setShowError(false);
        setErrorMessage("Bir hata meydana geldi! Lütfen daha sonra tekrar deneyin.");
        setIsLoading(true);

        const code = generateRandomSixDigitNumber().toString();
        const pwd = generateRandomSixDigitNumber().toString();
        const smsPhone = normalizeNationalNumber(phone, countryCode); // SMS +90 olmadan gönderilir

        Services.GetToken()
            .then((res) => {
                if (!res.data.status) throw new Error("Token alınamadı");
                const t = res.data.result.token;
                setToken(t);
                setGeneratedPassword(pwd);
                setSentOtp(code);
                return Services.SendSms(t, smsPhone, buildOtpMessage(code));
            })
            .then((res) => {
                setIsLoading(false);
                if (res?.data?.status) {
                    setOtp("");
                    setCurrentStep(1);
                    setTekrarKalan(TEKRAR_GONDER_SN);
                } else {
                    setShowError(true);
                    setErrorMessage(cokDenemeMi(res?.data?.message) ? COK_DENEME_MESAJI : "Doğrulama kodu gönderilemedi. Telefon numaranızı kontrol edip tekrar deneyin.");
                }
            })
            .catch(() => {
                setIsLoading(false);
                setShowError(true);
            });
    };

    // Kodu tekrar gönder.
    const handleResendOtp = () => {
        if (isLoading || tekrarKalan > 0) return;
        setShowError(false);
        setIsLoading(true);
        const code = generateRandomSixDigitNumber().toString();
        const smsPhone = normalizeNationalNumber(phone, countryCode); // SMS +90 olmadan gönderilir
        const ensure = token
            ? Promise.resolve(token)
            : Services.GetToken().then((res) => {
                if (!res.data.status) throw new Error("Token alınamadı");
                setToken(res.data.result.token);
                return res.data.result.token;
            });
        ensure
            .then((t) => Services.SendSms(t, smsPhone, buildOtpMessage(code)))
            .then((res) => {
                setIsLoading(false);
                if (res?.data?.status) { setSentOtp(code); setOtp(""); setOtpResetKey((k) => k + 1); setTekrarKalan(TEKRAR_GONDER_SN); }
                else {
                    setShowError(true);
                    setErrorMessage(cokDenemeMi(res?.data?.message) ? COK_DENEME_MESAJI : "Kod tekrar gönderilemedi. Lütfen tekrar deneyin.");
                    if (cokDenemeMi(res?.data?.message)) setTekrarKalan(TEKRAR_GONDER_SN);
                }
            })
            .catch(() => { setIsLoading(false); setShowError(true); });
    };

    // 2. ADIM: OTP doğruysa kaydı tamamla.
    const handleVerifyAndRegister = () => {
        if (!sentOtp || otp.trim().length !== 6) return;
        if (otp.trim() !== String(sentOtp)) {
            setShowError(true);
            setErrorMessage("Girdiğiniz doğrulama kodu hatalı. Lütfen tekrar deneyin.");
            return;
        }
        setShowError(false);
        setIsLoading(true);
        Services.UserRegister(
            token,
            name,
            lastname,
            generatedPassword,
            email,
            normalizeNationalNumber(phone, countryCode),
            "+" + normalizeCountryCode(countryCode),
            { gdprConfirmation: kvkkAgreement, termsOfUseConfirmation: termsAgreement },
            registerType,
            isCustomerPortal ? "" : applicationNote.trim()
        )
            .then((res) => {
                setIsLoading(false);
                if (res.data.status) {
                    setShowSuccess(true);
                } else {
                    setShowError(true);
                    if (res.data.message && res.data.message.includes("already")) {
                        setErrorMessage("Bu e-posta adresi veya telefon numarası ile bir hesap zaten mevcut!");
                    }
                }
            })
            .catch(() => { setIsLoading(false); setShowError(true); });
    };

    return (
        <div className="signup-form">
            <div className="logo-container">
                <img src={logo} alt="Troy Depo" />
            </div>

            <div className="hello-message">
                <span
                    style={{
                        fontWeight: "600",
                        color: "rgba(10, 34, 82, 1)",
                        fontSize: "32px"
                    }}
                >
                    {copy.title}
                </span>
                <span
                    style={{
                        fontWeight: "500",
                        color: "rgba(159, 164, 170, 1)",
                        fontSize: "16px",
                        textAlign:'center'
                    }}
                >
                    {copy.subtitle}
                </span>
            </div>

            {currentStep === 0 && (<>
            <div className="signup-form-input-holder">
                <div
                    style={{
                        width: "100%",
                        flexDirection: "row",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "9px"
                    }}
                >
                    <CustomInput
                        value={name}
                        onChange={(e) => setName(capitalizeWords(e.target.value))}
                        placeholder={"Ad"}
                    />
                    <CustomInput
                        value={lastname}
                        onChange={(e) => setLastname(capitalizeWords(e.target.value))}
                        placeholder={"Soyad"}
                    />
                </div>

                <CustomInput
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={validateEmail}
                    placeholder={"E-posta Adresi"}
                />
                {errors.email && (
                    <span
                        style={{
                            color: "red",
                            fontSize: "12px",
                            alignSelf: "flex-start"
                        }}
                    >
                        {errors.email}
                    </span>
                )}

                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "9px",
                        width: "100%"
                    }}
                >
                    <PhoneInput
                        variant="custom"
                        countryCode={countryCode}
                        phone={phone}
                        onChange={({ countryCode: nextCc, phone: nextPhone }) => {
                            setCountryCode(nextCc);
                            setPhone(nextPhone);
                        }}
                        countryCodeStyle={{ width: "110px" }}
                        phoneStyle={{ flex: 1 }}
                    />
                </div>
                {errors.phone && (
                    <span style={{ color: "red", fontSize: "12px" }}>
                        {errors.phone}
                    </span>
                )}
            </div>

            {!isCustomerPortal && (
                <div className="signup-application-note">
                    <label className="signup-application-note-label" htmlFor="application-note">
                        Başvurunuz ile ilgili kısaca bahseder misiniz? (Başvuru yeriniz, konu vb.)
                    </label>
                    <textarea
                        id="application-note"
                        value={applicationNote}
                        onChange={(e) => setApplicationNote(e.target.value)}
                        placeholder="Örn. İstanbul Tuzla'da deposu olan bir nakliye firmasıyız, partner ağınıza katılmak istiyoruz."
                        maxLength={1000}
                        rows={4}
                    />
                </div>
            )}

            <div
                style={{
                    flexDirection: "column",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "13px",
                    marginTop: "19px",
                    marginBottom: "34px"
                }}
            >
                <ContractAgreementCheckbox
                    checked={kvkkAgreement}
                    onAccepted={setKvkkAgreement}
                    token={token}
                    uri="kisisel-verilerin-korunmasi-ve-gizlilik-politikasi"
                    title="Kişisel Verilerin Korunması ve Gizlilik Politikası"
                >
                    <span className="remember-me">Kişisel Verilerin Korunması ve Gizlilik Politikası'nı okudum onaylıyorum.</span>
                </ContractAgreementCheckbox>
                <ContractAgreementCheckbox
                    checked={termsAgreement}
                    onAccepted={setTermsAgreement}
                    token={token}
                    uri="kullanim-sartlari"
                    title="Kullanıcı Sözleşmesi"
                >
                    <span className="remember-me">Kullanıcı Sözleşmesi'ni okudum onaylıyorum.</span>
                </ContractAgreementCheckbox>
            </div>
            </>)}

            {currentStep === 1 && (
                <div className="signup-form-input-holder" style={{ marginBottom: "34px" }}>
                    <span style={{ color: "rgba(159, 164, 170, 1)", fontSize: "14px", textAlign: "center" }}>
                        <strong>{"+" + normalizeCountryCode(countryCode) + " " + normalizeNationalNumber(phone, countryCode)}</strong> numarasına gönderdiğimiz 6 haneli doğrulama kodunu girin.
                    </span>
                    <div style={{ width: "100%", marginTop: "20px" }}>
                        <OTPInput key={`otp-${otpResetKey}`} length={6} onChangeOTP={(arr) => setOtp(arr.join(""))} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", width: "100%", marginTop: "18px" }}>
                        <span role="button" tabIndex={0} onKeyDown={klavyeIleTikla} onClick={() => { setCurrentStep(0); setShowError(false); }} style={{ color: "rgba(159, 164, 170, 1)", fontWeight: 600, cursor: "pointer", fontSize: "13px" }}>← Geri</span>
                        <span role="button" tabIndex={tekrarKalan > 0 ? -1 : 0} aria-disabled={tekrarKalan > 0} onKeyDown={klavyeIleTikla} onClick={handleResendOtp} style={{ color: tekrarKalan > 0 ? "rgba(159, 164, 170, 1)" : "rgba(80, 185, 100, 1)", fontWeight: 600, cursor: tekrarKalan > 0 ? "default" : "pointer", fontSize: "13px" }}>
                            {tekrarKalan > 0 ? `Kodu Tekrar Gönder (${tekrarKalan} sn)` : "Kodu Tekrar Gönder"}
                        </span>
                    </div>
                </div>
            )}

            {!isLoading ? (
                <button
                    className="signup-button"
                    disabled={currentStep === 0 ? !allFieldsAreValid() : otp.trim().length !== 6}
                    onClick={currentStep === 0 ? handleSendOtp : handleVerifyAndRegister}
                >
                    {currentStep === 0 ? copy.button : "Doğrula ve Tamamla"}
                </button>
            ) : (
                <button
                    className="signup-button"
                    disabled
                    style={{
                        justifyContent: "center",
                        alignItems: "center",
                        display: "flex"
                    }}
                >
                    <Spinner size={25} />
                </button>
            )}

            {showError && (
                <div
                    style={{
                        flexDirection: "row",
                        display: "flex",
                        alignItems: "center",
                        marginTop: "10px",
                        marginBottom: "34px"
                    }}
                >
                    <span
                        style={{
                            fontSize: "14px",
                            fontFamily: "Plus Jakarta Sans",
                            color: "#FF3333"
                        }}
                    >
                        {errorMessage}
                    </span>
                </div>
            )}

            <div className="have-account">
                <span
                    style={{
                        color: "rgba(159, 164, 170, 1)",
                        fontWeight: "500"
                    }}
                >
                    Hesabınız var mı?
                </span>
                <span role="button" tabIndex={0} onKeyDown={klavyeIleTikla}
                    onClick={() => navigate("/")}
                    style={{
                        color: "rgba(80, 185, 100, 1)",
                        fontWeight: "600",
                        cursor: "pointer"
                    }}
                >
                    Giriş Yap
                </span>
            </div>

            <span className="copyright">
                ©Copyright 2025. All Right Reserved
            </span>

            <SuccessModal
                isOpen={showSuccess}
                title={copy.successTitle}
                message={copy.successMessage}
                onConfirm={() => {
                    setShowSuccess(false);
                    navigate("/");
                }}
                onConfirmText="Tamam"
            />
        </div>
    );
};

export default SignupFormV2;
